import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bus,
  Calendar,
  Clock,
  MapPin,
  CheckCircle2,
  XCircle,
  Pencil,
  ExternalLink,
  Users,
  Truck,
  UserCheck,
  Briefcase,
  AlertCircle,
  RefreshCw,
  StickyNote,
  Map as MapIcon,
  QrCode,
} from "lucide-react";
import {
  fetchBusTastings,
  fetchContacts,
  scheduleBusTasting,
  updateBusTasting,
  completeBusTasting,
  cancelBusTasting,
  computeRouteTimeline,
  createTrip,
} from "../lib/api";
import { FeedbackQRDialog } from "../components/FeedbackQRDialog";
import { FeedbackInbox } from "../components/FeedbackInbox";
import { useAuth } from "../lib/AuthProvider";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

function relativeDate(iso) {
  if (!iso) return null;
  const d = new Date(iso + (iso.length === 10 ? "T00:00" : ""));
  if (isNaN(d)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  const days = Math.round((target.getTime() - today.getTime()) / 86400000);
  const dateStr = d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: target.getFullYear() !== today.getFullYear() ? "numeric" : undefined });
  if (days === 0) return { label: `${dateStr} · TODAY`, tone: "due" };
  if (days === 1) return { label: `${dateStr} · TOMORROW`, tone: "due" };
  if (days < 0) return { label: `${dateStr} · ${Math.abs(days)}d ago`, tone: "overdue" };
  if (days < 7) return { label: `${dateStr} · in ${days}d`, tone: "soon" };
  return { label: dateStr, tone: "future" };
}

function fmtTime(t) {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  if (isNaN(h)) return t;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m || 0).padStart(2, "0")} ${ampm}`;
}

export default function BusTastings() {
  const { role } = useAuth();
  const isAdmin = role === "admin";
  const [tastings, setTastings] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // tasting object or null
  const [qrTasting, setQrTasting] = useState(null); // tasting | null — opens feedback QR dialog

  const load = async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([
        fetchBusTastings(),
        fetchContacts().catch(() => []),
      ]);
      setTastings(t);
      setContacts(c);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const drivers = useMemo(() => contacts.filter((c) => c.role === "driver"), [contacts]);
  const hosts = useMemo(() => contacts.filter((c) => c.role === "host"), [contacts]);
  const leadHosts = useMemo(() => contacts.filter((c) => c.role === "lead_host"), [contacts]);

  const { needsScheduling, needsByDate, needsNoDate, upcoming, byDate } = useMemo(() => {
    const needs = [];
    const up = [];
    for (const t of tastings || []) {
      if (t.status === "requested") needs.push(t);
      else if (t.status === "scheduled") up.push(t);
    }
    // Split requested into dated + undated, group dated by requested_date
    const dated = {};
    const undated = [];
    for (const t of needs) {
      if (t.requested_date) {
        (dated[t.requested_date] = dated[t.requested_date] || []).push(t);
      } else {
        undated.push(t);
      }
    }
    // Soonest first for the keys
    const sortedDatedEntries = Object.entries(dated).sort((a, b) =>
      a[0].localeCompare(b[0])
    );
    // Within a day, sort by requested_time then dispensary name
    for (const [, list] of sortedDatedEntries) {
      list.sort((a, b) =>
        (a.requested_time || "99:99").localeCompare(b.requested_time || "99:99") ||
        a.dispensary_name.localeCompare(b.dispensary_name)
      );
    }
    undated.sort((a, b) => a.dispensary_name.localeCompare(b.dispensary_name));
    // Sort upcoming by scheduled date
    up.sort((a, b) => {
      const ad = a.scheduled_date || "9999-12-31";
      const bd = b.scheduled_date || "9999-12-31";
      return ad.localeCompare(bd) || (a.scheduled_time || "").localeCompare(b.scheduled_time || "");
    });
    // Group upcoming by date for the "X events on Jun 12 — plan route" hint
    const grouped = {};
    for (const t of up) {
      const d = t.scheduled_date;
      if (!d) continue;
      (grouped[d] = grouped[d] || []).push(t);
    }
    return {
      needsScheduling: needs,
      needsByDate: sortedDatedEntries,
      needsNoDate: undated,
      upcoming: up,
      byDate: grouped,
    };
  }, [tastings]);

  // Per-day selection state for the bulk-schedule flow. Keyed by
  // requested_date (or "__nodate__") → Set<tasting.id>.
  const [selection, setSelection] = useState({});
  const toggleSelect = (groupKey, id) => {
    setSelection((prev) => {
      const next = { ...prev };
      const cur = new Set(next[groupKey] || []);
      cur.has(id) ? cur.delete(id) : cur.add(id);
      next[groupKey] = cur;
      return next;
    });
  };
  const clearSelection = (groupKey) => {
    setSelection((prev) => ({ ...prev, [groupKey]: new Set() }));
  };
  const clearAllSelections = () => setSelection({});
  // Bulk scheduling dialog state
  const [bulkScheduling, setBulkScheduling] = useState(null); // { date, tastings[] } | null
  // Phase-B: multi-day trip dialog state
  const [tripScheduling, setTripScheduling] = useState(null); // { tastingsByDate } | null

  // Which dated groups have ≥1 selection? Used to light up the sticky
  // "Plan N-day trip" affordance when selection spans multiple days.
  // Ignore the "__nodate__" bucket — trips need real dates.
  const multiDaySelection = useMemo(() => {
    const buckets = {};
    for (const [groupKey, idSet] of Object.entries(selection)) {
      if (groupKey === "__nodate__" || !idSet || idSet.size === 0) continue;
      const list = (needsByDate.find(([d]) => d === groupKey) || [])[1] || [];
      const picked = list.filter((t) => idSet.has(t.id));
      if (picked.length > 0) buckets[groupKey] = picked;
    }
    const dates = Object.keys(buckets).sort();
    const totalStops = dates.reduce((n, d) => n + buckets[d].length, 0);
    return { dates, buckets, totalStops };
  }, [selection, needsByDate]);

  return (
    <div className="max-w-[1600px] mx-auto space-y-5 fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Bus className="w-6 h-6 text-emerald-400" />
            Bus Events
          </h2>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Dispensary requests come in here. Schedule a date + crew, plan the
            route on the map, then mark completed when the day's done.
          </p>
        </div>
        <Button
          data-testid="bus-tastings-refresh"
          size="sm"
          variant="outline"
          onClick={load}
          disabled={loading}
          className="border-zinc-800 bg-zinc-950 hover:bg-zinc-900 text-xs h-9"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* SECTION 1: Needs Scheduling — grouped by sales-requested date */}
      <Section
        title="Needs scheduling"
        icon={AlertCircle}
        accent="text-amber-300"
        count={needsScheduling.length}
        testid="section-needs-scheduling"
      >
        {needsScheduling.length === 0 ? (
          <EmptyState message="No outstanding requests — you're all caught up." />
        ) : (
          <div className="space-y-3">
            {multiDaySelection.dates.length >= 2 && (
              <div
                data-testid="trip-plan-banner"
                className="sticky top-2 z-20 flex items-center justify-between gap-3 flex-wrap panel border-emerald-500/50 bg-emerald-500/10 px-4 py-3 shadow-lg shadow-emerald-500/5"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="shrink-0 w-8 h-8 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
                    <Bus className="w-4 h-4 text-emerald-300" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-emerald-100 font-medium">
                      {multiDaySelection.totalStops} stop{multiDaySelection.totalStops === 1 ? "" : "s"} selected across {multiDaySelection.dates.length} days
                    </div>
                    <div className="text-[11px] text-emerald-200/70 font-mono mt-0.5 truncate">
                      {multiDaySelection.dates.join(" · ")}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={clearAllSelections}
                    data-testid="trip-plan-banner-clear"
                    className="text-[11px] text-zinc-400 hover:text-zinc-200 underline underline-offset-2"
                  >
                    clear
                  </button>
                  <Button
                    size="sm"
                    onClick={() => setTripScheduling({ buckets: multiDaySelection.buckets })}
                    data-testid="plan-trip-button"
                    className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 h-8 text-xs"
                  >
                    <Calendar className="w-3.5 h-3.5 mr-1.5" />
                    Plan {multiDaySelection.dates.length}-day trip
                  </Button>
                </div>
              </div>
            )}
            {needsByDate.map(([date, list]) => {
              const groupKey = date;
              const sel = selection[groupKey] || new Set();
              return (
                <RequestDateGroup
                  key={date}
                  date={date}
                  tastings={list}
                  selected={sel}
                  onToggle={(id) => toggleSelect(groupKey, id)}
                  onClear={() => clearSelection(groupKey)}
                  onBulkSchedule={() => {
                    const picks = list.filter((t) => sel.has(t.id));
                    if (picks.length === 0) return;
                    setBulkScheduling({ date, tastings: picks });
                  }}
                  onEditSingle={(t) => setEditing(t)}
                  onCancelSingle={async (t) => {
                    if (!window.confirm(`Cancel bus event request from ${t.dispensary_name}?`)) return;
                    await cancelBusTasting(t.id);
                    toast.success("Request cancelled");
                    load();
                  }}
                />
              );
            })}
            {needsNoDate.length > 0 && (
              <RequestDateGroup
                date={null}
                tastings={needsNoDate}
                selected={selection["__nodate__"] || new Set()}
                onToggle={(id) => toggleSelect("__nodate__", id)}
                onClear={() => clearSelection("__nodate__")}
                onBulkSchedule={() => {
                  const sel = selection["__nodate__"] || new Set();
                  const picks = needsNoDate.filter((t) => sel.has(t.id));
                  if (picks.length === 0) return;
                  setBulkScheduling({ date: null, tastings: picks });
                }}
                onEditSingle={(t) => setEditing(t)}
                onCancelSingle={async (t) => {
                  if (!window.confirm(`Cancel bus event request from ${t.dispensary_name}?`)) return;
                  await cancelBusTasting(t.id);
                  toast.success("Request cancelled");
                  load();
                }}
              />
            )}
          </div>
        )}
      </Section>

      {/* SECTION 2: Upcoming */}
      <Section
        title="Upcoming"
        icon={Calendar}
        accent="text-emerald-300"
        count={upcoming.length}
        testid="section-upcoming"
      >
        {upcoming.length === 0 ? (
          <EmptyState message="Nothing on the calendar. Schedule a request above to get started." />
        ) : (
          <div className="space-y-4">
            {Object.entries(byDate).map(([date, list]) => {
              // When every scheduled event in a given day belongs to the
              // same trip, surface the trip name + "Day X of Y" on the
              // date header so dispatchers see the trip grouping.
              const tripIds = Array.from(new Set(list.map((t) => t.trip_id).filter(Boolean)));
              const tripInfo = (tripIds.length === 1 && list[0].trip_name)
                ? { id: tripIds[0], name: list[0].trip_name, dayNum: list[0].trip_day_number, total: list[0].trip_total_days }
                : null;
              return (
              <div key={date} data-testid={`upcoming-date-${date}`}>
                <DateHeader date={date} count={list.length} ids={list.map((t) => t.dispensary_id)} tripInfo={tripInfo} />
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mt-2">
                  {list.map((t) => (
                    <ScheduledCard
                      key={t.id}
                      t={t}
                      onEdit={() => setEditing(t)}
                      onShowQR={() => setQrTasting(t)}
                      onComplete={async () => {
                        if (!window.confirm(`Mark bus event at ${t.dispensary_name} as completed? This will log it on the dispensary timeline.`)) return;
                        await completeBusTasting(t.id);
                        toast.success("Tasting marked completed");
                        load();
                      }}
                      onCancel={async () => {
                        if (!window.confirm(`Cancel scheduled bus event at ${t.dispensary_name}?`)) return;
                        await cancelBusTasting(t.id);
                        toast.success("Tasting cancelled");
                        load();
                      }}
                    />
                  ))}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </Section>

      {editing && (
        <ScheduleDialog
          tasting={editing}
          drivers={drivers}
          hosts={hosts}
          leadHosts={leadHosts}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {bulkScheduling && (
        <BulkScheduleDialog
          date={bulkScheduling.date}
          tastings={bulkScheduling.tastings}
          drivers={drivers}
          hosts={hosts}
          leadHosts={leadHosts}
          onClose={() => setBulkScheduling(null)}
          onSaved={() => {
            // Clear selections for the groups we just scheduled and reload.
            const groupKey = bulkScheduling.date || "__nodate__";
            clearSelection(groupKey);
            setBulkScheduling(null);
            load();
          }}
        />
      )}

      {tripScheduling && (
        <TripScheduleDialog
          buckets={tripScheduling.buckets}
          drivers={drivers}
          hosts={hosts}
          leadHosts={leadHosts}
          onClose={() => setTripScheduling(null)}
          onSaved={() => {
            clearAllSelections();
            setTripScheduling(null);
            load();
          }}
        />
      )}

      <FeedbackQRDialog
        open={!!qrTasting}
        onOpenChange={(o) => { if (!o) setQrTasting(null); }}
        tasting={qrTasting}
      />

      {/* Admin-only feedback inbox at the bottom of the page. Hosts
          and other roles see their feedback mentions through their
          weekly Recap printout, not here. */}
      {isAdmin && <FeedbackInbox />}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

function Section({ title, icon: Icon, accent, count, testid, children }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${accent}`} />
        <h3 className={`text-sm font-semibold uppercase tracking-[0.12em] font-mono ${accent}`}>
          {title}
        </h3>
        <span className="text-[10px] font-mono text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-full px-2 py-0.5">
          {count}
        </span>
      </div>
      {children}
    </section>
  );
}

function EmptyState({ message }) {
  return (
    <div className="panel p-8 text-center text-sm text-zinc-500">
      {message}
    </div>
  );
}

function DateHeader({ date, count, ids, tripInfo }) {
  const rel = relativeDate(date);
  const planUrl = `/map?tasting_date=${date}`;
  return (
    <div
      data-testid={`date-header-${date}`}
      className="flex items-center justify-between gap-2 flex-wrap px-3 py-2 bg-zinc-900/60 border border-zinc-800 rounded-md"
    >
      <div className="flex items-center gap-2 text-xs flex-wrap">
        <Calendar className="w-3.5 h-3.5 text-emerald-300" />
        <span className={`font-mono font-semibold ${
          rel?.tone === "overdue" ? "text-rose-300"
          : rel?.tone === "due"   ? "text-amber-300"
          : rel?.tone === "soon"  ? "text-emerald-300"
          : "text-zinc-300"
        }`}>
          {rel?.label || date}
        </span>
        <span className="text-zinc-500">·</span>
        <span className="text-zinc-300">
          {count} bus event{count === 1 ? "" : "s"}
        </span>
        {tripInfo && (
          tripInfo.id ? (
            <Link
              to={`/trips/${tripInfo.id}`}
              data-testid={`date-header-trip-${date}`}
              className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded bg-violet-500/15 border border-violet-500/40 text-violet-200 hover:bg-violet-500/25 hover:text-violet-100 transition-colors"
              title={`Open trip detail · ${tripInfo.total || "?"}-day trip`}
            >
              <Bus className="w-3 h-3" />
              {tripInfo.name}
              {tripInfo.dayNum && tripInfo.total && (
                <span className="text-violet-300/80">
                  · Day {tripInfo.dayNum} of {tripInfo.total}
                </span>
              )}
            </Link>
          ) : (
            <span
              data-testid={`date-header-trip-${date}`}
              className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded bg-violet-500/15 border border-violet-500/40 text-violet-200"
              title={`Part of a ${tripInfo.total || "?"}-day trip`}
            >
              <Bus className="w-3 h-3" />
              {tripInfo.name}
              {tripInfo.dayNum && tripInfo.total && (
                <span className="text-violet-300/80">
                  · Day {tripInfo.dayNum} of {tripInfo.total}
                </span>
              )}
            </span>
          )
        )}
      </div>
      <Link
        to={planUrl}
        data-testid={`plan-day-route-${date}`}
        className="inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:text-emerald-200 font-mono"
        title="Open Map filtered to this day's bus events, with all stops pre-selected"
      >
        <MapIcon className="w-3.5 h-3.5" />
        Plan day's route →
      </Link>
    </div>
  );
}

function RequestedCard({ t, onSchedule, onCancel }) {
  const rel = relativeDate(t.requested_date);
  return (
    <div
      data-testid={`requested-card-${t.id}`}
      className="panel p-3.5 space-y-2 hover:border-amber-500/30 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <Link to={`/dispensaries/${t.dispensary_id}`} className="block flex-1 min-w-0 group">
          <div className="text-sm font-medium text-zinc-50 group-hover:text-emerald-300 truncate flex items-center gap-1">
            {t.dispensary_name}
            <ExternalLink className="w-3 h-3 text-zinc-700 group-hover:text-zinc-400" />
          </div>
          <div className="text-[11px] text-zinc-500 truncate flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {t.dispensary_city || "—"}{t.dispensary_county ? ` · ${t.dispensary_county}` : ""}
          </div>
        </Link>
      </div>

      {(t.requested_date || t.requested_time) && (
        <div
          data-testid={`requested-when-${t.id}`}
          className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 rounded border border-amber-500/30 bg-amber-500/10 text-amber-200"
        >
          <Calendar className="w-3 h-3" />
          Sales requested:
          {t.requested_date && (
            <span className={
              rel?.tone === "overdue" ? "text-rose-300 font-semibold"
              : rel?.tone === "due"   ? "text-amber-100 font-semibold"
              : ""
            }>
              {rel?.label || t.requested_date}
            </span>
          )}
          {t.requested_time && <>
            <Clock className="w-3 h-3 ml-1" />
            {fmtTime(t.requested_time)}
          </>}
        </div>
      )}

      {t.requested_notes && (
        <div className="text-xs text-zinc-300 leading-snug whitespace-pre-wrap break-words bg-zinc-950/50 border border-zinc-800 rounded p-2">
          <StickyNote className="w-3 h-3 inline mr-1 text-zinc-500" />
          {t.requested_notes}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-1 border-t border-zinc-800/60">
        <Button
          size="sm"
          variant="outline"
          onClick={onCancel}
          data-testid={`cancel-requested-${t.id}`}
          className="border-zinc-800 bg-zinc-950 hover:bg-zinc-900 text-xs h-7"
        >
          <XCircle className="w-3 h-3 mr-1" />
          Cancel
        </Button>
        <Link
          to={`/map?focus=${t.dispensary_id}`}
          data-testid={`view-map-requested-${t.id}`}
          className="inline-flex items-center gap-1 text-xs bg-zinc-900 border border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-200 text-zinc-300 rounded h-7 px-2.5 font-mono"
          title="View this dispensary on the map"
        >
          <MapPin className="w-3 h-3" />
          Map
        </Link>
        <Button
          size="sm"
          onClick={onSchedule}
          data-testid={`schedule-${t.id}`}
          className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 rounded text-xs h-7"
        >
          <Calendar className="w-3 h-3 mr-1" />
          Schedule
        </Button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Grouped-by-date request cards (new multi-select scheduling flow)
// -----------------------------------------------------------------------------

/**
 * Wraps every requested bus event for a single `requested_date` (or the
 * no-date bucket when `date` is null). Hot-day styling kicks in at ≥2
 * requests. Multi-select is scoped to this card; the floating action bar
 * at the bottom appears once at least one row is selected.
 */
function RequestDateGroup({
  date,
  tastings,
  selected,
  onToggle,
  onClear,
  onBulkSchedule,
  onEditSingle,
  onCancelSingle,
}) {
  const rel = date ? relativeDate(date) : null;
  const hot = tastings.length >= 2;
  const selectedCount = selected.size;
  const planUrl = date ? `/map?tasting_date=${date}` : null;
  const groupKey = date || "nodate";

  // Hot days get an amber accent + 🔥 badge. Everything else uses a
  // calmer zinc border so the eye lands on the busy day first.
  const borderCls = hot
    ? "border-amber-500/40 shadow-[0_0_0_1px_rgba(245,158,11,0.08)]"
    : "border-zinc-800";

  // Which reps are requesting on this day? Shown as chips so the
  // dispatcher sees at a glance that it's a Sherry+Ricky day vs a
  // single-rep day.
  const requesters = Array.from(
    new Set(
      tastings
        .map((t) => t.requested_by_name || t.requested_by_email)
        .filter(Boolean)
    )
  );

  return (
    <div
      data-testid={`request-group-${groupKey}`}
      className={`panel ${borderCls} overflow-hidden`}
    >
      {/* Group header: date + heat + Plan route link ------------------ */}
      <div className="flex items-center justify-between flex-wrap gap-2 px-4 py-3 border-b border-zinc-800/80 bg-zinc-900/40">
        <div className="flex items-center gap-2.5 flex-wrap">
          <Calendar className={`w-4 h-4 ${hot ? "text-amber-300" : "text-emerald-300"}`} />
          <span
            data-testid={`request-group-date-${groupKey}`}
            className={`text-sm font-semibold font-mono ${
              rel?.tone === "overdue" ? "text-rose-300"
              : rel?.tone === "due"   ? "text-amber-200"
              : hot                   ? "text-amber-200"
              : "text-zinc-200"
            }`}
          >
            {date ? (rel?.label || date) : "No date requested"}
          </span>
          <span
            className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${
              hot
                ? "bg-amber-500/15 border-amber-500/40 text-amber-200"
                : "bg-zinc-900 border-zinc-800 text-zinc-400"
            }`}
          >
            {tastings.length} request{tastings.length === 1 ? "" : "s"}
          </span>
          {hot && (
            <span
              className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-500/40 text-amber-200"
              title="Multiple reps have requested this day — priority"
            >
              Hot day
            </span>
          )}
          {requesters.length > 0 && (
            <span className="text-[11px] text-zinc-500">
              · {requesters.slice(0, 3).join(", ")}
              {requesters.length > 3 ? ` +${requesters.length - 3}` : ""}
            </span>
          )}
        </div>
        {planUrl && (
          <Link
            to={planUrl}
            data-testid={`plan-route-${groupKey}`}
            className="inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:text-emerald-200 font-mono"
            title="Open Map filtered to this day's requested stops"
          >
            <MapIcon className="w-3.5 h-3.5" />
            Plan day's route →
          </Link>
        )}
      </div>

      {/* Row list ---------------------------------------------------- */}
      <ul className="divide-y divide-zinc-800/60">
        {tastings.map((t) => (
          <SelectableRequestRow
            key={t.id}
            t={t}
            checked={selected.has(t.id)}
            onToggle={() => onToggle(t.id)}
            onEdit={() => onEditSingle(t)}
            onCancel={() => onCancelSingle(t)}
          />
        ))}
      </ul>

      {/* Action bar — appears when something is selected ------------- */}
      {selectedCount > 0 && (
        <div
          data-testid={`request-group-actions-${groupKey}`}
          className="flex items-center justify-between gap-2 px-4 py-2.5 bg-emerald-500/8 border-t border-emerald-500/30"
        >
          <div className="text-xs text-emerald-200 font-mono">
            {selectedCount} selected
            <button
              type="button"
              onClick={onClear}
              data-testid={`clear-selection-${groupKey}`}
              className="ml-2 text-zinc-500 hover:text-zinc-300 underline underline-offset-2"
            >
              clear
            </button>
          </div>
          <Button
            size="sm"
            onClick={onBulkSchedule}
            data-testid={`bulk-schedule-${groupKey}`}
            className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 h-8 text-xs"
          >
            <Calendar className="w-3.5 h-3.5 mr-1.5" />
            Schedule {selectedCount} as route
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Compact row inside a RequestDateGroup. Checkbox on the left for
 * multi-select; clicking anywhere else on the row opens the single-edit
 * dialog (the existing per-request flow still works).
 */
function SelectableRequestRow({ t, checked, onToggle, onEdit, onCancel }) {
  return (
    <li
      data-testid={`request-row-${t.id}`}
      className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${
        checked ? "bg-emerald-500/8" : "hover:bg-zinc-900/40"
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        data-testid={`request-checkbox-${t.id}`}
        aria-label={`Select request from ${t.dispensary_name}`}
        className="w-4 h-4 shrink-0 cursor-pointer accent-emerald-500"
      />
      <div className="flex-1 min-w-0">
        <Link
          to={`/dispensaries/${t.dispensary_id}`}
          className="group inline-flex items-center gap-1 text-sm font-medium text-zinc-100 hover:text-emerald-300 truncate max-w-full"
        >
          <span className="truncate">{t.dispensary_name}</span>
          <ExternalLink className="w-3 h-3 text-zinc-700 group-hover:text-zinc-400 shrink-0" />
        </Link>
        <div className="text-[11px] text-zinc-500 flex items-center gap-2 flex-wrap mt-0.5">
          <span className="inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {t.dispensary_city || "—"}
            {t.dispensary_county ? ` · ${t.dispensary_county}` : ""}
          </span>
          {t.requested_time && (
            <span className="inline-flex items-center gap-1 text-amber-300/80 font-mono">
              <Clock className="w-3 h-3" />
              Asked for {fmtTime(t.requested_time)}
            </span>
          )}
          {(t.requested_by_name || t.requested_by_email) && (
            <span className="inline-flex items-center gap-1 text-zinc-400">
              <UserCheck className="w-3 h-3" />
              {t.requested_by_name || t.requested_by_email}
            </span>
          )}
        </div>
        {t.requested_notes && (
          <div
            className="text-[11px] text-zinc-400 italic mt-1 line-clamp-2"
            title={t.requested_notes}
          >
            <StickyNote className="w-3 h-3 inline mr-1 text-zinc-600" />
            {t.requested_notes}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button
          size="sm"
          variant="ghost"
          onClick={onEdit}
          data-testid={`row-schedule-${t.id}`}
          title="Schedule this one on its own"
          className="h-7 text-xs text-zinc-400 hover:text-emerald-300 hover:bg-emerald-500/10 px-2"
        >
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onCancel}
          data-testid={`row-cancel-${t.id}`}
          title="Cancel this request"
          className="h-7 text-xs text-zinc-500 hover:text-rose-300 hover:bg-rose-500/10 px-2"
        >
          <XCircle className="w-3.5 h-3.5" />
        </Button>
      </div>
    </li>
  );
}



function ScheduledCard({ t, onEdit, onShowQR, onComplete, onCancel }) {
  const rel = relativeDate(t.scheduled_date);
  return (
    <div
      data-testid={`scheduled-card-${t.id}`}
      className="panel p-3.5 space-y-2 hover:border-emerald-500/30 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <Link to={`/dispensaries/${t.dispensary_id}`} className="block flex-1 min-w-0 group">
          <div className="text-sm font-medium text-zinc-50 group-hover:text-emerald-300 truncate flex items-center gap-1">
            {t.dispensary_name}
            <ExternalLink className="w-3 h-3 text-zinc-700 group-hover:text-zinc-400" />
          </div>
          <div className="text-[11px] text-zinc-500 truncate flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {t.dispensary_city || "—"}{t.dispensary_county ? ` · ${t.dispensary_county}` : ""}
          </div>
        </Link>
        {t.scheduled_time && (
          <div className="text-xs font-mono text-emerald-300 inline-flex items-center gap-1 shrink-0">
            <Clock className="w-3 h-3" />
            {fmtTime(t.scheduled_time)}
          </div>
        )}
      </div>

      {/* Crew chips */}
      <div className="flex flex-wrap gap-1.5">
        {t.driver_id && t.crew_names?.[t.driver_id] && (
          <CrewChip icon={Truck} label="Driver" name={t.crew_names[t.driver_id]} color="text-sky-200 border-sky-500/30 bg-sky-500/10" />
        )}
        {(t.host_ids || []).map((id) => t.crew_names?.[id] && (
          <CrewChip key={id} icon={UserCheck} label="Host" name={t.crew_names[id]} color="text-fuchsia-200 border-fuchsia-500/30 bg-fuchsia-500/10" />
        ))}
        {(t.lead_host_ids || []).map((id) => t.crew_names?.[id] && (
          <CrewChip key={id} icon={Briefcase} label="Mgr" name={t.crew_names[id]} color="text-amber-200 border-amber-500/30 bg-amber-500/10" />
        ))}
        {!t.driver_id && (t.host_ids || []).length === 0 && (t.lead_host_ids || []).length === 0 && (
          <div className="text-[11px] text-rose-300 italic inline-flex items-center gap-1">
            <Users className="w-3 h-3" />
            No crew assigned yet
          </div>
        )}
      </div>

      {(t.requested_notes || t.dispatcher_notes) && (
        <div className="text-xs text-zinc-300 leading-snug whitespace-pre-wrap break-words bg-zinc-950/50 border border-zinc-800 rounded p-2 space-y-1">
          {t.requested_notes && (
            <div><span className="text-zinc-500 font-mono text-[10px] uppercase">Request:</span> {t.requested_notes}</div>
          )}
          {t.dispatcher_notes && (
            <div><span className="text-zinc-500 font-mono text-[10px] uppercase">Notes:</span> {t.dispatcher_notes}</div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-1 border-t border-zinc-800/60 flex-wrap">
        <Button
          size="sm"
          variant="outline"
          onClick={onCancel}
          data-testid={`cancel-scheduled-${t.id}`}
          className="border-zinc-800 bg-zinc-950 hover:bg-zinc-900 text-xs h-7"
          title="Cancel this bus event"
        >
          <XCircle className="w-3 h-3 mr-1" />
          Cancel
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onShowQR}
          data-testid={`feedback-qr-btn-${t.id}`}
          className="border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/15 text-emerald-200 text-xs h-7"
          title="Get the anonymous feedback QR for the picture frame"
        >
          <QrCode className="w-3 h-3 mr-1" />
          Feedback QR
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onEdit}
          data-testid={`edit-scheduled-${t.id}`}
          className="border-zinc-800 bg-zinc-950 hover:bg-zinc-900 text-xs h-7"
        >
          <Pencil className="w-3 h-3 mr-1" />
          Edit
        </Button>
        <Link
          to={`/map?focus=${t.dispensary_id}`}
          data-testid={`view-map-scheduled-${t.id}`}
          className="inline-flex items-center gap-1 text-xs bg-zinc-900 border border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-200 text-zinc-300 rounded h-7 px-2.5 font-mono"
          title="View this dispensary on the map"
        >
          <MapPin className="w-3 h-3" />
          Map
        </Link>
        <Button
          size="sm"
          onClick={onComplete}
          data-testid={`complete-scheduled-${t.id}`}
          className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 rounded text-xs h-7"
        >
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Mark completed
        </Button>
      </div>

      {rel?.tone === "overdue" && (
        <div className="text-[10px] text-rose-300 font-mono uppercase tracking-wider inline-flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Overdue — review and complete or reschedule
        </div>
      )}
    </div>
  );
}

function CrewChip({ icon: Icon, label, name, color }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border ${color}`}>
      <Icon className="w-2.5 h-2.5" />
      <span className="opacity-60 uppercase">{label}</span>
      <span className="font-semibold">{name}</span>
    </span>
  );
}

// -----------------------------------------------------------------------------
// Schedule / edit dialog
// -----------------------------------------------------------------------------
function ScheduleDialog({ tasting, drivers, hosts, leadHosts, onClose, onSaved }) {
  const isScheduled = tasting.status === "scheduled";
  const [date, setDate] = useState(tasting.scheduled_date || tasting.requested_date || "");
  const [time, setTime] = useState(tasting.scheduled_time || tasting.requested_time || "");
  const [driverId, setDriverId] = useState(tasting.driver_id || "");
  const [hostIds, setHostIds] = useState(tasting.host_ids || []);
  const [leadHostIds, setLeadHostIds] = useState(tasting.lead_host_ids || []);
  const [dispatcherNotes, setDispatcherNotes] = useState(tasting.dispatcher_notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!date) {
      toast.error("Please pick a date");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        scheduled_date: date,
        scheduled_time: time || null,
        driver_id: driverId || null,
        host_ids: hostIds,
        lead_host_ids: leadHostIds,
        dispatcher_notes: dispatcherNotes,
      };
      if (isScheduled) {
        await updateBusTasting(tasting.id, payload);
        toast.success("Tasting updated");
      } else {
        await scheduleBusTasting(tasting.id, payload);
        toast.success("Tasting scheduled");
      }
      onSaved();
    } catch (e) {
      toast.error("Could not save", { description: String(e) });
    } finally {
      setSaving(false);
    }
  };

  const driverOptions = drivers;
  const hostOptions = hosts;
  const leadHostOptions = leadHosts;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bus className="w-5 h-5 text-emerald-400" />
            {isScheduled ? "Edit" : "Schedule"} bus event at {tasting.dispensary_name}
          </DialogTitle>
        </DialogHeader>

        {tasting.requested_date || tasting.requested_time || tasting.requested_notes ? (
          <div className="text-xs bg-amber-500/5 border border-amber-500/20 rounded-md p-3 space-y-1">
            <div className="text-amber-300 font-mono uppercase tracking-wider text-[10px]">Sales rep request</div>
            {(tasting.requested_date || tasting.requested_time) && (
              <div className="text-zinc-200">
                <Calendar className="w-3 h-3 inline mr-1" />
                {tasting.requested_date || "(no date)"}{tasting.requested_time ? ` at ${fmtTime(tasting.requested_time)}` : ""}
              </div>
            )}
            {tasting.requested_notes && (
              <div className="text-zinc-300 italic">"{tasting.requested_notes}"</div>
            )}
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-zinc-400">Date</Label>
            <Input
              data-testid="schedule-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Time</Label>
            <Input
              data-testid="schedule-time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9 mt-1"
            />
          </div>
        </div>

        <div className="space-y-3">
          <CrewSection
            icon={Truck}
            color="text-sky-300"
            label="Driver"
            hint="Single driver — one per bus event."
          >
            <Select
              value={driverId || "_none"}
              onValueChange={(v) => setDriverId(v === "_none" ? "" : v)}
            >
              <SelectTrigger
                data-testid="schedule-driver"
                className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9 text-sm"
              >
                <SelectValue placeholder={driverOptions.length ? "Pick a driver…" : "No drivers in Contacts yet"} />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectItem value="_none">— Not assigned —</SelectItem>
                {driverOptions.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CrewSection>
          <CrewSection
            icon={UserCheck}
            color="text-fuchsia-300"
            label="Hosts"
            hint="Multiple hosts allowed — staff running the bus event on the bus."
          >
            <ChipPicker
              testid="schedule-hosts"
              options={hostOptions}
              values={hostIds}
              onChange={setHostIds}
              emptyHint="No hosts in Contacts yet. Add some in Settings."
              chipColor="text-fuchsia-200 border-fuchsia-500/30 bg-fuchsia-500/10"
            />
          </CrewSection>
          <CrewSection
            icon={Briefcase}
            color="text-amber-300"
            label="Lead Hosts"
            hint="Multiple lead hosts allowed — in charge at the event."
          >
            <ChipPicker
              testid="schedule-lead-hosts"
              options={leadHostOptions}
              values={leadHostIds}
              onChange={setLeadHostIds}
              emptyHint="No lead hosts in Contacts yet. Add some in Settings."
              chipColor="text-amber-200 border-amber-500/30 bg-amber-500/10"
            />
          </CrewSection>
        </div>

        <div>
          <Label className="text-xs text-zinc-400">Dispatcher notes (private)</Label>
          <Textarea
            data-testid="schedule-notes"
            value={dispatcherNotes}
            onChange={(e) => setDispatcherNotes(e.target.value)}
            placeholder="Special prep, route notes, parking quirks…"
            rows={3}
            className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 text-xs font-mono"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-800 bg-zinc-950 hover:bg-zinc-900">
            Cancel
          </Button>
          <Button
            data-testid="schedule-save"
            onClick={save}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-500"
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
            {saving ? "Saving…" : isScheduled ? "Save changes" : "Schedule bus event"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CrewSection({ icon: Icon, color, label, hint, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className={`w-3.5 h-3.5 ${color}`} />
        <Label className={`text-xs ${color}`}>{label}</Label>
        <span className="text-[10px] text-zinc-500 italic">{hint}</span>
      </div>
      {children}
    </div>
  );
}

/**
 * Compact multi-select expressed as toggleable chips. Better UX than a
 * dropdown when the list is short (typically 0-10 hosts/managers) and
 * lets the dispatcher see everyone available at a glance.
 */
function ChipPicker({ testid, options, values, onChange, emptyHint, chipColor }) {
  if (options.length === 0) {
    return (
      <div className="text-[11px] text-zinc-500 italic px-2 py-1.5 border border-dashed border-zinc-800 rounded">
        {emptyHint}
      </div>
    );
  }
  const set = new Set(values || []);
  const toggle = (id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };
  return (
    <div data-testid={testid} className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const active = set.has(o.id);
        return (
          <button
            key={o.id}
            type="button"
            data-testid={`${testid}-chip-${o.id}`}
            onClick={() => toggle(o.id)}
            className={`text-xs font-mono px-2 py-1 rounded border transition-colors ${
              active
                ? chipColor + " font-semibold"
                : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
            }`}
          >
            {active && "✓ "}
            {o.name}
          </button>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Bulk schedule dialog
// -----------------------------------------------------------------------------

/**
 * Schedules N requested bus events in one shot. One date, one crew, one
 * dispatcher-notes field — but each stop keeps its own time slot so the
 * driver has a sensible per-shop ETA. On submit we hit the existing
 * per-tasting /schedule endpoint in parallel so no backend change is
 * needed; after everything settles we fire a toast with the success /
 * failure count and bounce to the Map pre-filtered to that day.
 */
function BulkScheduleDialog({
  date: initialDate,
  tastings,
  drivers,
  hosts,
  leadHosts,
  onClose,
  onSaved,
}) {
  const navigate = useNavigate();
  const [date, setDate] = useState(initialDate || "");
  const [startTime, setStartTime] = useState(() => {
    // Seed the start time from the earliest sales-rep-requested time;
    // if nobody asked for a time, default to 10:00 AM.
    const times = tastings
      .map((t) => t.requested_time)
      .filter(Boolean)
      .sort();
    return times[0] || "10:00";
  });
  const [driverId, setDriverId] = useState("");
  const [hostIds, setHostIds] = useState([]);
  const [leadHostIds, setLeadHostIds] = useState([]);
  const [dispatcherNotes, setDispatcherNotes] = useState("");
  // Per-stop config: duration (minutes), extended-stay flag, notes.
  // Default is 180 min / 3h — the "free visit" window.
  const [stopCfg, setStopCfg] = useState(() => {
    const m = {};
    for (const t of tastings) {
      m[t.id] = {
        duration: t.stop_duration_minutes || 180,
        extended: !!t.is_extended_stay,
        extendedNotes: t.extended_stay_notes || "",
      };
    }
    return m;
  });
  const [timeline, setTimeline] = useState(null); // { stops, data_source } | null
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const updateStop = (id, patch) =>
    setStopCfg((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  // Which stops are missing coords? We still schedule those, but can't
  // compute travel time to/from them.
  const missingCoords = tastings.filter(
    (t) => t.dispensary_latitude == null || t.dispensary_longitude == null
  );
  const canComputeTimeline =
    missingCoords.length === 0 && startTime && tastings.length > 0;

  // Fetch the timeline whenever the inputs that affect it change.
  useEffect(() => {
    if (!canComputeTimeline) {
      setTimeline(null);
      return;
    }
    let cancelled = false;
    setTimelineLoading(true);
    const payload = {
      start_time: startTime,
      stops: tastings.map((t) => ({
        dispensary_id: t.id, // tasting.id is unique even if the same shop appears twice
        latitude: t.dispensary_latitude,
        longitude: t.dispensary_longitude,
        stop_duration_minutes: stopCfg[t.id]?.duration || 180,
      })),
    };
    computeRouteTimeline(payload)
      .then((data) => {
        if (!cancelled) setTimeline(data);
      })
      .catch(() => {
        if (!cancelled) setTimeline(null);
      })
      .finally(() => {
        if (!cancelled) setTimelineLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Stringify stopCfg so useEffect compares deeply enough.
  }, [startTime, canComputeTimeline, JSON.stringify(stopCfg), tastings]);

  // Flag any stop whose own duration would push arrival past a hard
  // real-world constraint (none right now, but hook is here).
  const timelineByStop = useMemo(() => {
    const m = {};
    (timeline?.stops || []).forEach((s, i) => {
      m[tastings[i].id] = s;
    });
    return m;
  }, [timeline, tastings]);

  const save = async () => {
    if (!date) {
      toast.error("Pick a date for this route");
      return;
    }
    setSaving(true);
    const basePayload = {
      scheduled_date: date,
      driver_id: driverId || null,
      host_ids: hostIds,
      lead_host_ids: leadHostIds,
      dispatcher_notes: dispatcherNotes || "",
    };
    const results = await Promise.allSettled(
      tastings.map((t) => {
        const cfg = stopCfg[t.id] || {};
        const tl = timelineByStop[t.id];
        return scheduleBusTasting(t.id, {
          ...basePayload,
          scheduled_time: tl?.arrive_at || null,
          stop_duration_minutes: cfg.duration || 180,
          is_extended_stay: !!cfg.extended,
          extended_stay_notes: cfg.extended ? (cfg.extendedNotes || "") : "",
        });
      })
    );
    const failures = results.filter((r) => r.status === "rejected");
    setSaving(false);
    if (failures.length === 0) {
      toast.success(
        `Scheduled ${tastings.length} bus event${tastings.length === 1 ? "" : "s"} for ${date}`
      );
      onSaved();
      navigate(`/map?tasting_date=${date}`);
    } else if (failures.length < tastings.length) {
      toast.warning(
        `${tastings.length - failures.length} scheduled · ${failures.length} failed — refresh and retry the rest`
      );
      onSaved();
    } else {
      toast.error("Could not schedule any of these — nothing was saved.", {
        description: String(failures[0].reason || ""),
      });
    }
  };

  const driverOptions = drivers || [];
  const hostOptions = hosts || [];
  const leadHostOptions = leadHosts || [];

  const dayEndHhmm =
    timeline?.stops?.length
      ? timeline.stops[timeline.stops.length - 1].leave_by
      : null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="bulk-schedule-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-4xl max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bus className="w-5 h-5 text-emerald-400" />
            Schedule {tastings.length} bus event{tastings.length === 1 ? "" : "s"} as a route
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-zinc-400 -mt-2 leading-relaxed">
          Pick the date and the <strong>start time of your first stop</strong>.
          Every arrival and leave-by time below is computed from the real
          road-network travel between stops. The default stop is{" "}
          <strong>3 hours</strong> — toggle "Extended paid stay" if the
          shop has paid for a longer visit.
        </p>

        {/* Date + start time ----------------------------------------- */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr] gap-3">
          <div>
            <Label className="text-xs text-zinc-400">Date</Label>
            <Input
              data-testid="bulk-schedule-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">
              Start time (first stop)
            </Label>
            <Input
              data-testid="bulk-schedule-start-time"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9 mt-1"
            />
          </div>
        </div>

        {/* Timeline — the headline UI --------------------------------- */}
        <div className="pt-1 space-y-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-[11px] font-mono uppercase tracking-wider text-emerald-300 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              Route timeline
              {timelineLoading && (
                <span className="text-zinc-500 normal-case tracking-normal">
                  computing…
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-500">
              {timeline?.data_source === "osrm_live" && (
                <span className="text-emerald-400">
                  live road routing
                </span>
              )}
              {timeline?.data_source === "haversine_fallback" && (
                <span className="text-amber-300" title="OSRM unreachable — using straight-line estimate">
                  rough estimate (routing offline)
                </span>
              )}
              {timeline?.data_source === "mixed" && (
                <span className="text-amber-300">partial live data</span>
              )}
              {dayEndHhmm && (
                <span className="text-zinc-300">
                  Day ends ~{fmtTime(dayEndHhmm)}
                </span>
              )}
            </div>
          </div>

          {missingCoords.length > 0 && (
            <div className="text-[11px] px-2.5 py-1.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200">
              <AlertCircle className="w-3 h-3 inline mr-1" />
              {missingCoords.length} stop{missingCoords.length === 1 ? "" : "s"}{" "}
              missing lat/lng — travel time can't be auto-computed. Open the
              dispensary and run <strong>Find on map</strong> to geocode.
            </div>
          )}

          <ol className="rounded-md border border-zinc-800 divide-y divide-zinc-800 bg-zinc-950">
            {tastings.map((t, idx) => {
              const cfg = stopCfg[t.id] || { duration: 180, extended: false, extendedNotes: "" };
              const tl = timelineByStop[t.id];
              const nextTl = timelineByStop[tastings[idx + 1]?.id];
              const wasAsked = t.requested_time && tl?.arrive_at && tl.arrive_at !== t.requested_time;
              return (
                <li
                  key={t.id}
                  data-testid={`bulk-stop-${t.id}`}
                  className="px-3.5 py-3 space-y-2"
                >
                  <div className="flex items-start gap-3 flex-wrap">
                    <span className="text-[10px] font-mono text-zinc-500 w-6 mt-1 shrink-0">
                      #{idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-zinc-100 truncate">
                        {t.dispensary_name}
                        {cfg.extended && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 align-middle">
                            💰 Extended
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-zinc-500 truncate">
                        {t.dispensary_city || "—"}
                        {t.requested_by_name ? ` · asked by ${t.requested_by_name}` : ""}
                        {t.requested_time ? ` · wanted ${fmtTime(t.requested_time)}` : ""}
                      </div>
                    </div>
                    {tl && (
                      <div
                        data-testid={`bulk-stop-timeline-${t.id}`}
                        className="shrink-0 text-right text-[11px] font-mono"
                      >
                        <div className="text-emerald-200">
                          Arrive {fmtTime(tl.arrive_at)}
                        </div>
                        <div className="text-zinc-400">
                          Leave {fmtTime(tl.leave_by)}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap pl-9">
                    <Label className="text-[11px] text-zinc-500 font-mono">
                      Stop length
                    </Label>
                    <Select
                      value={String(cfg.duration)}
                      onValueChange={(v) =>
                        updateStop(t.id, { duration: parseInt(v, 10) })
                      }
                    >
                      <SelectTrigger
                        data-testid={`bulk-stop-duration-${t.id}`}
                        className="bg-zinc-900 border-zinc-800 text-zinc-100 h-7 w-[108px] text-xs"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                        <SelectItem value="90">1.5 h</SelectItem>
                        <SelectItem value="120">2 h</SelectItem>
                        <SelectItem value="150">2.5 h</SelectItem>
                        <SelectItem value="180">3 h (default)</SelectItem>
                        <SelectItem value="210">3.5 h</SelectItem>
                        <SelectItem value="240">4 h</SelectItem>
                        <SelectItem value="300">5 h</SelectItem>
                        <SelectItem value="360">6 h</SelectItem>
                        <SelectItem value="480">8 h (full day)</SelectItem>
                      </SelectContent>
                    </Select>
                    <label className="inline-flex items-center gap-1.5 text-[11px] text-zinc-400 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cfg.extended}
                        onChange={(e) => {
                          const extended = e.target.checked;
                          updateStop(t.id, {
                            extended,
                            // Auto-bump duration when they turn it on so the
                            // flag actually buys them more time.
                            duration: extended && cfg.duration <= 180 ? 240 : cfg.duration,
                          });
                        }}
                        data-testid={`bulk-stop-extended-${t.id}`}
                        className="w-3.5 h-3.5 accent-emerald-500"
                      />
                      Extended paid stay
                    </label>
                    {wasAsked && (
                      <span
                        className="text-[10px] font-mono text-amber-300"
                        title={`Rep asked for ${fmtTime(t.requested_time)}; route puts us here at ${fmtTime(tl.arrive_at)}`}
                      >
                        ≠ rep's requested time
                      </span>
                    )}
                  </div>
                  {cfg.extended && (
                    <Input
                      value={cfg.extendedNotes}
                      onChange={(e) =>
                        updateStop(t.id, { extendedNotes: e.target.value })
                      }
                      placeholder="What they paid for (half-day, 4h, special promo…)"
                      data-testid={`bulk-stop-extended-notes-${t.id}`}
                      className="bg-zinc-900 border-emerald-500/30 text-zinc-100 h-7 text-xs ml-9"
                    />
                  )}
                  {/* Travel-to-next chip */}
                  {idx < tastings.length - 1 && (
                    <div className="pl-9">
                      {tl?.travel_minutes_to_next != null ? (
                        <div className="inline-flex items-center gap-1.5 text-[11px] font-mono text-zinc-500">
                          <Truck className="w-3 h-3 text-zinc-600" />
                          ~{tl.travel_minutes_to_next} min to{" "}
                          <span className="text-zinc-400">
                            {tastings[idx + 1].dispensary_name}
                          </span>
                          {!tl.travel_live && (
                            <span className="text-amber-400/70" title="Fell back to straight-line estimate">
                              (est)
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="text-[11px] text-zinc-600 italic">
                          travel time unavailable
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </div>

        {/* Crew ------------------------------------------------------- */}
        <div className="space-y-3">
          <CrewSection
            icon={Truck}
            color="text-sky-300"
            label="Driver"
            hint="Single driver for the whole route."
          >
            <Select
              value={driverId || "_none"}
              onValueChange={(v) => setDriverId(v === "_none" ? "" : v)}
            >
              <SelectTrigger
                data-testid="bulk-schedule-driver"
                className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9"
              >
                <SelectValue placeholder={driverOptions.length ? "Pick a driver…" : "No drivers in Contacts yet"} />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectItem value="_none">— Not assigned —</SelectItem>
                {driverOptions.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CrewSection>

          <CrewSection
            icon={Users}
            color="text-emerald-300"
            label="Hosts"
            hint="Everyone working the bus — select as many as you need."
          >
            <ChipPicker
              testid="bulk-schedule-hosts"
              options={hostOptions}
              values={hostIds}
              onChange={setHostIds}
              emptyHint="No hosts in Contacts yet."
              chipColor="bg-emerald-500/15 border-emerald-500/40 text-emerald-200"
            />
          </CrewSection>

          <CrewSection
            icon={UserCheck}
            color="text-violet-300"
            label="Lead host"
            hint="Point person — usually senior sales/manager."
          >
            <ChipPicker
              testid="bulk-schedule-leads"
              options={leadHostOptions}
              values={leadHostIds}
              onChange={setLeadHostIds}
              emptyHint="No lead hosts in Contacts yet."
              chipColor="bg-violet-500/15 border-violet-500/40 text-violet-200"
            />
          </CrewSection>
        </div>

        {/* Shared dispatcher notes ------------------------------------ */}
        <div>
          <Label className="text-xs text-zinc-400">
            Dispatcher notes (applied to every stop)
          </Label>
          <Textarea
            data-testid="bulk-schedule-notes"
            value={dispatcherNotes}
            onChange={(e) => setDispatcherNotes(e.target.value)}
            placeholder="Meet at depot 9am, hit all stops, back by 6pm."
            className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 text-sm"
            rows={2}
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
            className="border-zinc-800 bg-zinc-950 hover:bg-zinc-900 text-zinc-300"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving || !date}
            data-testid="bulk-schedule-submit"
            className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50"
          >
            {saving
              ? "Scheduling…"
              : `Approve & schedule ${tastings.length} stop${tastings.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// -----------------------------------------------------------------------------
// Phase-B · TripScheduleDialog — multi-day bus-event route
// -----------------------------------------------------------------------------
// Takes selections spread across ≥2 days (passed in as `buckets` keyed
// by date), lets the dispatcher name the trip, flag overnight stays,
// assign a shared crew once, tweak per-stop durations/extended toggles,
// see live OSRM travel estimates per day, and submit everything in a
// single POST /api/trips. On success it navigates to the Map pre-filtered
// to the FIRST day of the trip so the dispatcher can eyeball day 1
// immediately; the Upcoming view below shows the trip badge on every
// day card.
// -----------------------------------------------------------------------------

function TripScheduleDialog({ buckets, drivers, hosts, leadHosts, onClose, onSaved }) {
  const navigate = useNavigate();
  const dates = Object.keys(buckets).sort();
  const totalStops = dates.reduce((n, d) => n + buckets[d].length, 0);

  // Trip-level fields
  const [name, setName] = useState(
    dates.length ? `${dates[0]} → ${dates[dates.length - 1]} trip` : "Trip"
  );
  const [overnightStays, setOvernightStays] = useState(true);
  const [overnightNotes, setOvernightNotes] = useState("");
  const [driverId, setDriverId] = useState("");
  const [hostIds, setHostIds] = useState([]);
  const [leadHostIds, setLeadHostIds] = useState([]);
  const [dispatcherNotes, setDispatcherNotes] = useState("");

  // Per-day start time
  const [startTimes, setStartTimes] = useState(() => {
    const m = {};
    for (const d of dates) {
      const list = buckets[d];
      const earliest = list.map((t) => t.requested_time).filter(Boolean).sort()[0];
      m[d] = earliest || "10:00";
    }
    return m;
  });

  // Per-stop config
  const [stopCfg, setStopCfg] = useState(() => {
    const m = {};
    for (const d of dates) {
      for (const t of buckets[d]) {
        m[t.id] = {
          duration: t.stop_duration_minutes || 180,
          extended: !!t.is_extended_stay,
          extendedNotes: t.extended_stay_notes || "",
        };
      }
    }
    return m;
  });
  const updateStop = (id, patch) =>
    setStopCfg((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  // Per-day timeline fetch — one API call per day
  const [timelines, setTimelines] = useState({}); // { date → { stops, data_source } }
  const [loadingDays, setLoadingDays] = useState(new Set());
  const [saving, setSaving] = useState(false);

  // Which days are missing coords for any of their stops?
  const missingCoordsByDay = useMemo(() => {
    const m = {};
    for (const d of dates) {
      m[d] = buckets[d].filter(
        (t) => t.dispensary_latitude == null || t.dispensary_longitude == null
      );
    }
    return m;
  }, [dates, buckets]);

  // Recompute every day's timeline whenever any input changes.
  // Keyed-on stringified state so React runs this effect reliably.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const next = {};
      const loading = new Set();
      for (const d of dates) {
        if (missingCoordsByDay[d].length > 0) continue;
        loading.add(d);
      }
      setLoadingDays(loading);
      await Promise.all(dates.map(async (d) => {
        if (missingCoordsByDay[d].length > 0) {
          next[d] = null;
          return;
        }
        try {
          const payload = {
            start_time: startTimes[d] || "10:00",
            stops: buckets[d].map((t) => ({
              dispensary_id: t.id,
              latitude: t.dispensary_latitude,
              longitude: t.dispensary_longitude,
              stop_duration_minutes: stopCfg[t.id]?.duration || 180,
            })),
          };
          const data = await computeRouteTimeline(payload);
          if (!cancelled) next[d] = data;
        } catch (e) {
          if (!cancelled) next[d] = null;
        }
      }));
      if (!cancelled) {
        setTimelines(next);
        setLoadingDays(new Set());
      }
    };
    run();
    return () => { cancelled = true; };
  }, [JSON.stringify(startTimes), JSON.stringify(stopCfg), dates.join(","), JSON.stringify(missingCoordsByDay)]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: name.trim() || `${dates[0]} → ${dates[dates.length - 1]} trip`,
        overnight_stays: overnightStays,
        overnight_notes: overnightNotes || "",
        driver_id: driverId || null,
        host_ids: hostIds,
        lead_host_ids: leadHostIds,
        dispatcher_notes: dispatcherNotes || "",
        days: dates.map((d) => ({
          date: d,
          start_time: startTimes[d] || "10:00",
          stops: buckets[d].map((t) => ({
            bus_tasting_id: t.id,
            stop_duration_minutes: stopCfg[t.id]?.duration || 180,
            is_extended_stay: !!stopCfg[t.id]?.extended,
            extended_stay_notes: stopCfg[t.id]?.extended ? (stopCfg[t.id]?.extendedNotes || "") : "",
          })),
        })),
      };
      await createTrip(payload);
      toast.success(`Scheduled "${payload.name}" — ${totalStops} stops across ${dates.length} days`);
      onSaved();
      navigate(`/map?tasting_date=${dates[0]}`);
    } catch (e) {
      toast.error("Could not schedule trip", { description: e?.response?.data?.detail || String(e) });
    } finally {
      setSaving(false);
    }
  };

  const driverOptions = drivers || [];
  const hostOptions = hosts || [];
  const leadHostOptions = leadHosts || [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="trip-schedule-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-4xl max-h-[94vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bus className="w-5 h-5 text-violet-400" />
            Plan {dates.length}-day trip · {totalStops} stops
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-zinc-400 -mt-2 leading-relaxed">
          One crew, one trip name, overnight stays rolled in. Each day
          has its own start time and timeline. The same OSRM road data
          powers travel estimates between stops.
        </p>

        {/* Trip name + overnight ------------------------------------- */}
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-3">
          <div>
            <Label className="text-xs text-zinc-400">Trip name</Label>
            <Input
              data-testid="trip-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. SoCal Expo, Humboldt Harvest Run"
              className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Days</Label>
            <div className="h-9 mt-1 flex items-center text-sm font-mono text-zinc-300 px-3 bg-zinc-900/60 border border-zinc-800 rounded-md">
              {dates[0]} → {dates[dates.length - 1]} · {dates.length} days
            </div>
          </div>
        </div>

        <div className="rounded-md border border-zinc-800 bg-zinc-900/40 px-3 py-2.5 space-y-2">
          <label className="flex items-center gap-2 text-sm text-zinc-200 cursor-pointer">
            <input
              type="checkbox"
              checked={overnightStays}
              onChange={(e) => setOvernightStays(e.target.checked)}
              data-testid="trip-overnight-toggle"
              className="w-4 h-4 accent-violet-500"
            />
            <span>Crew is staying overnight on this trip</span>
          </label>
          {overnightStays && (
            <Input
              data-testid="trip-overnight-notes"
              value={overnightNotes}
              onChange={(e) => setOvernightNotes(e.target.value)}
              placeholder="e.g. Marriott Costa Mesa — book 2 rooms Thu + Fri"
              className="bg-zinc-900 border-violet-500/30 text-zinc-100 h-8 text-xs"
            />
          )}
        </div>

        {/* Shared crew ----------------------------------------------- */}
        <div className="space-y-3">
          <CrewSection
            icon={Truck}
            color="text-sky-300"
            label="Driver"
            hint="One driver for the whole trip."
          >
            <Select
              value={driverId || "_none"}
              onValueChange={(v) => setDriverId(v === "_none" ? "" : v)}
            >
              <SelectTrigger
                data-testid="trip-schedule-driver"
                className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9"
              >
                <SelectValue placeholder={driverOptions.length ? "Pick a driver…" : "No drivers in Contacts yet"} />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectItem value="_none">— Not assigned —</SelectItem>
                {driverOptions.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CrewSection>

          <CrewSection
            icon={Users}
            color="text-emerald-300"
            label="Hosts"
            hint="Crew working the bus — select everyone travelling."
          >
            <ChipPicker
              testid="trip-schedule-hosts"
              options={hostOptions}
              values={hostIds}
              onChange={setHostIds}
              emptyHint="No hosts in Contacts yet."
              chipColor="bg-emerald-500/15 border-emerald-500/40 text-emerald-200"
            />
          </CrewSection>

          <CrewSection
            icon={UserCheck}
            color="text-violet-300"
            label="Lead host"
            hint="Point person for the whole trip."
          >
            <ChipPicker
              testid="trip-schedule-leads"
              options={leadHostOptions}
              values={leadHostIds}
              onChange={setLeadHostIds}
              emptyHint="No lead hosts in Contacts yet."
              chipColor="bg-violet-500/15 border-violet-500/40 text-violet-200"
            />
          </CrewSection>
        </div>

        {/* Per-day sections ------------------------------------------ */}
        <div className="space-y-4">
          {dates.map((date, dayIdx) => {
            const stops = buckets[date];
            const tl = timelines[date];
            const loading = loadingDays.has(date);
            const dayEnd = tl?.stops?.length ? tl.stops[tl.stops.length - 1].leave_by : null;
            return (
              <div
                key={date}
                data-testid={`trip-day-${date}`}
                className="panel border-violet-500/30 bg-violet-500/5 p-4 space-y-3"
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded bg-violet-500/20 border border-violet-500/40 text-violet-100">
                      Day {dayIdx + 1}
                    </span>
                    <span className="text-sm font-mono text-zinc-100">{date}</span>
                    <span className="text-xs text-zinc-500">· {stops.length} stop{stops.length === 1 ? "" : "s"}</span>
                    {loading && <span className="text-[10px] font-mono text-zinc-500">computing…</span>}
                    {tl?.data_source === "osrm_live" && !loading && (
                      <span className="text-[10px] font-mono text-emerald-400">live road routing</span>
                    )}
                    {tl?.data_source === "haversine_fallback" && !loading && (
                      <span className="text-[10px] font-mono text-amber-300">rough estimate</span>
                    )}
                    {dayEnd && (
                      <span className="text-[10px] font-mono text-zinc-400">· ends ~{fmtTime(dayEnd)}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-[11px] text-zinc-500 font-mono">Start</Label>
                    <Input
                      data-testid={`trip-day-start-${date}`}
                      type="time"
                      value={startTimes[date] || ""}
                      onChange={(e) => setStartTimes((prev) => ({ ...prev, [date]: e.target.value }))}
                      className="bg-zinc-900 border-zinc-800 text-zinc-100 h-8 w-[110px] text-xs"
                    />
                  </div>
                </div>

                {missingCoordsByDay[date].length > 0 && (
                  <div className="text-[11px] px-2.5 py-1.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    {missingCoordsByDay[date].length} stop{missingCoordsByDay[date].length === 1 ? "" : "s"} missing lat/lng — travel time can't be auto-computed.
                  </div>
                )}

                <ol className="rounded-md border border-zinc-800 divide-y divide-zinc-800 bg-zinc-950/60">
                  {stops.map((t, idx) => {
                    const cfg = stopCfg[t.id] || { duration: 180, extended: false, extendedNotes: "" };
                    const stopTl = tl?.stops?.[idx];
                    return (
                      <li
                        key={t.id}
                        data-testid={`trip-stop-${t.id}`}
                        className="px-3.5 py-2.5 space-y-2"
                      >
                        <div className="flex items-start gap-3 flex-wrap">
                          <span className="text-[10px] font-mono text-zinc-500 w-6 mt-1 shrink-0">
                            #{idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-zinc-100 truncate">
                              {t.dispensary_name}
                              {cfg.extended && (
                                <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 align-middle">
                                  💰 Extended
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-zinc-500 truncate">
                              {t.dispensary_city || "—"}
                              {t.requested_by_name ? ` · asked by ${t.requested_by_name}` : ""}
                            </div>
                          </div>
                          {stopTl && (
                            <div className="shrink-0 text-right text-[11px] font-mono">
                              <div className="text-emerald-200">Arrive {fmtTime(stopTl.arrive_at)}</div>
                              <div className="text-zinc-400">Leave {fmtTime(stopTl.leave_by)}</div>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap pl-9">
                          <Label className="text-[11px] text-zinc-500 font-mono">Stop length</Label>
                          <Select
                            value={String(cfg.duration)}
                            onValueChange={(v) => updateStop(t.id, { duration: parseInt(v, 10) })}
                          >
                            <SelectTrigger
                              data-testid={`trip-stop-duration-${t.id}`}
                              className="bg-zinc-900 border-zinc-800 text-zinc-100 h-7 w-[108px] text-xs"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                              <SelectItem value="90">1.5 h</SelectItem>
                              <SelectItem value="120">2 h</SelectItem>
                              <SelectItem value="150">2.5 h</SelectItem>
                              <SelectItem value="180">3 h (default)</SelectItem>
                              <SelectItem value="210">3.5 h</SelectItem>
                              <SelectItem value="240">4 h</SelectItem>
                              <SelectItem value="300">5 h</SelectItem>
                              <SelectItem value="360">6 h</SelectItem>
                              <SelectItem value="480">8 h (full day)</SelectItem>
                            </SelectContent>
                          </Select>
                          <label className="inline-flex items-center gap-1.5 text-[11px] text-zinc-400 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={cfg.extended}
                              onChange={(e) => {
                                const extended = e.target.checked;
                                updateStop(t.id, {
                                  extended,
                                  duration: extended && cfg.duration <= 180 ? 240 : cfg.duration,
                                });
                              }}
                              data-testid={`trip-stop-extended-${t.id}`}
                              className="w-3.5 h-3.5 accent-emerald-500"
                            />
                            Extended paid stay
                          </label>
                        </div>
                        {cfg.extended && (
                          <Input
                            value={cfg.extendedNotes}
                            onChange={(e) => updateStop(t.id, { extendedNotes: e.target.value })}
                            placeholder="What they paid for"
                            data-testid={`trip-stop-extended-notes-${t.id}`}
                            className="bg-zinc-900 border-emerald-500/30 text-zinc-100 h-7 text-xs ml-9"
                          />
                        )}
                        {idx < stops.length - 1 && stopTl?.travel_minutes_to_next != null && (
                          <div className="pl-9 inline-flex items-center gap-1.5 text-[11px] font-mono text-zinc-500">
                            <Truck className="w-3 h-3 text-zinc-600" />
                            ~{stopTl.travel_minutes_to_next} min to{" "}
                            <span className="text-zinc-400">{stops[idx + 1].dispensary_name}</span>
                            {!stopTl.travel_live && (
                              <span className="text-amber-400/70">(est)</span>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>
            );
          })}
        </div>

        {/* Dispatcher notes ------------------------------------------ */}
        <div>
          <Label className="text-xs text-zinc-400">Dispatcher notes (applied to every stop)</Label>
          <Textarea
            data-testid="trip-schedule-notes"
            value={dispatcherNotes}
            onChange={(e) => setDispatcherNotes(e.target.value)}
            placeholder="e.g. Load up Thursday night, debrief call Monday 9am."
            className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 text-sm"
            rows={2}
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
            className="border-zinc-800 bg-zinc-950 hover:bg-zinc-900 text-zinc-300"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving || !name.trim() || totalStops === 0}
            data-testid="trip-schedule-submit"
            className="bg-violet-600 hover:bg-violet-500 text-violet-50"
          >
            {saving
              ? "Scheduling…"
              : `Approve & schedule ${dates.length}-day trip`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

